import { useMemo, useState } from "react";
import { Plus, Trash2, Download } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { PageHeader } from "@/components/ui/page-header.js";
import { Select } from "@/components/ui/select.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { useToast } from "@/components/ui/toast.js";
import { DataTable } from "@/views/data-table.js";
import { ApiError } from "@/lib/api.js";
import type { TenantClosure, ClosureSeedName } from "@/lib/types.js";

/**
 * Closures admin page (PRD closures-and-holidays §"Calendar UI banner" / §"In").
 *
 * - Lists active tenant closures with their next occurrences within the window.
 * - Create/edit dialog with the four canonical fields: reason, rrule, dtstart,
 *   duration_minutes (+ optional until). Recurring closures use RFC 5545 rrule.
 * - Seed import dropdown (REQ-CL-011): three curated seeds.
 *
 * The calendar grid in `routes/calendar.tsx` consumes the same listClosures()
 * API and renders a diagonal-stripe overlay on closed days.
 */
export function ClosuresPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const closures = useAsync(() => api.listClosures(defaultRangeQuery()), [api]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TenantClosure | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const rows = closures.data?.data ?? [];
  useKeyboardShortcuts({
    c: () => setCreating(true),
    j: () => setSelectedId(moveSelection(rows, selectedId, 1)),
    k: () => setSelectedId(moveSelection(rows, selectedId, -1)),
    e: () => {
      const row = rows.find((r) => r.id === selectedId);
      if (row) setEditing(row);
    }
  });

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow="Schedule"
        title="Closures"
        description="Tenant-wide closed days. Closures cascade to all resources and apply to the booking engine the moment they're saved."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImporting(true)}>
              <Download className="h-4 w-4" />
              Import seed
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New closure
            </Button>
          </div>
        }
      />
      {closures.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <DataTable<TenantClosure>
          rows={rows}
          getRowKey={(r) => r.id}
          selectedRowKey={selectedId}
          onRowClick={(r) => setSelectedId(r.id)}
          columns={[
            { header: "Reason", cell: (r) => r.reason },
            {
              header: "Source",
              cell: (r) => (
                <Badge variant={r.source === "tenant_authored" ? "outline" : "secondary"}>
                  {r.source}
                </Badge>
              )
            },
            {
              header: "When",
              cell: (r) => (
                <span className="text-xs text-muted-foreground">{describeWhen(r)}</span>
              )
            },
            {
              header: "Next",
              cell: (r) => {
                const next = r.occurrences_in_window[0];
                if (!next) return <span className="text-muted-foreground">—</span>;
                return <span className="tabular-nums text-xs">{new Date(next.from).toLocaleDateString()}</span>;
              }
            },
            {
              header: "",
              className: "w-20 text-right",
              cell: (r) => (
                <div className="flex justify-end gap-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(r);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete ${r.reason}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await api.deleteClosure(r.id);
                        toast({ title: "Closure deleted", variant: "success" });
                        closures.reload();
                      } catch (error) {
                        toast({ title: "Could not delete", description: String(error), variant: "destructive" });
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )
            }
          ]}
        />
      )}

      <ClosureDialog
        open={creating}
        onOpenChange={setCreating}
        mode="create"
        onSaved={() => {
          closures.reload();
          setCreating(false);
        }}
      />
      <ClosureDialog
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        mode="edit"
        initial={editing}
        onSaved={() => {
          closures.reload();
          setEditing(null);
        }}
      />
      <ImportSeedDialog
        open={importing}
        onOpenChange={setImporting}
        onImported={() => {
          closures.reload();
          setImporting(false);
        }}
      />
    </div>
  );
}

function ClosureDialog({
  open,
  onOpenChange,
  mode,
  initial,
  onSaved
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: "create" | "edit";
  initial?: TenantClosure | null;
  onSaved: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New closure" : `Edit ${initial?.reason ?? "closure"}`}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const reason = String(form.get("reason") ?? "").trim();
            const dtstart = String(form.get("dtstart") ?? "").trim();
            const rrule = String(form.get("rrule") ?? "").trim();
            const duration_minutes = Number(form.get("duration_minutes") ?? 1440);
            const until = String(form.get("until") ?? "").trim();
            const body = {
              reason,
              dtstart: toIsoOrEmpty(dtstart),
              duration_minutes,
              rrule: rrule.length > 0 ? rrule : null,
              until: until.length > 0 ? toIsoOrEmpty(until) : null
            };
            if (!body.reason || !body.dtstart) {
              toast({ title: "Reason and dtstart are required", variant: "destructive" });
              return;
            }
            setSubmitting(true);
            try {
              if (mode === "edit" && initial) {
                await api.updateClosure(initial.id, body);
                toast({ title: "Closure updated", variant: "success" });
              } else {
                await api.createClosure(body);
                toast({ title: "Closure created", variant: "success" });
              }
              onSaved();
            } catch (error) {
              if (error instanceof ApiError && error.code === "invalid_rrule") {
                toast({ title: "Invalid RRULE", description: "Check the recurrence rule syntax.", variant: "destructive" });
              } else {
                toast({ title: "Could not save", description: String(error), variant: "destructive" });
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="reason">Reason</Label>
            <Input id="reason" name="reason" required defaultValue={initial?.reason ?? ""} maxLength={200} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="dtstart">Starts (ISO datetime, tenant-tz aware)</Label>
            <Input
              id="dtstart"
              name="dtstart"
              required
              placeholder="2026-12-25T00:00:00Z"
              defaultValue={initial?.dtstart ?? ""}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="duration_minutes">Duration (minutes)</Label>
            <Input
              id="duration_minutes"
              name="duration_minutes"
              type="number"
              min={1}
              max={525600}
              defaultValue={String(initial?.duration_minutes ?? 1440)}
            />
            <p className="text-[11px] text-muted-foreground">1440 = full day. 600 = half-day (10h).</p>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="rrule">RRULE (optional, RFC 5545)</Label>
            <Input
              id="rrule"
              name="rrule"
              placeholder="FREQ=YEARLY;BYMONTH=12;BYMONTHDAY=25"
              defaultValue={initial?.rrule ?? ""}
            />
            <p className="text-[11px] text-muted-foreground">Leave blank for one-off closures.</p>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="until">Until (optional, stops the recurrence)</Label>
            <Input
              id="until"
              name="until"
              placeholder="2030-12-31T23:59:59Z"
              defaultValue={initial?.until ?? ""}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportSeedDialog({
  open,
  onOpenChange,
  onImported
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onImported: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const seeds: ClosureSeedName[] = ["US_FEDERAL_HOLIDAYS", "UK_BANK_HOLIDAYS", "EU_COMMON_OBSERVANCES"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import seed closures</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const seed = String(form.get("seed") ?? "") as ClosureSeedName;
            setSubmitting(true);
            try {
              const result = await api.importClosureSeed(seed);
              if (result.created === 0) {
                toast({ title: "No new closures", description: "Seed already imported." });
              } else {
                toast({
                  title: `Imported ${result.created} closures`,
                  variant: "success"
                });
              }
              onImported();
            } catch (error) {
              toast({ title: "Could not import", description: String(error), variant: "destructive" });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="seed">Seed catalog</Label>
            <Select id="seed" name="seed" defaultValue={seeds[0]}>
              {seeds.map((seed) => (
                <option key={seed} value={seed}>
                  {seed.replaceAll("_", " ")}
                </option>
              ))}
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Re-importing the same seed is safe — duplicates are skipped (matched by reason + dtstart).
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function moveSelection<T extends { id: string }>(
  rows: T[],
  current: string | null,
  delta: 1 | -1
): string | null {
  if (rows.length === 0) return null;
  const idx = current ? rows.findIndex((r) => r.id === current) : -1;
  const nextIdx = idx === -1 ? (delta === 1 ? 0 : rows.length - 1) : Math.max(0, Math.min(rows.length - 1, idx + delta));
  return rows[nextIdx]!.id;
}

function describeWhen(closure: TenantClosure): string {
  if (closure.rrule) return `Recurring: ${closure.rrule}`;
  return `One-off: ${new Date(closure.dtstart).toLocaleDateString()}`;
}

function toIsoOrEmpty(value: string): string {
  if (!value) return "";
  // The closure API accepts both zone-qualified ISO ("2026-12-25T00:00:00Z")
  // and naked-local ISO ("2026-12-25T00:00:00") via z.string().datetime({
  // local: true }) — the backend interprets naked-local strings in the
  // tenant's primary_tz. Don't append "Z" to a naked datetime-local value,
  // because that pins the wall-clock to UTC and shifts the closure for any
  // non-UTC tenant. Pass naked-local values through unchanged.
  const hasZone = value.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(value);
  if (!hasZone) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

/** Default `from..to` window: today → +180 days. Keeps the helper under
 * the 365d cap (REQ-CL-005) and matches a reasonable admin browse horizon. */
function defaultRangeQuery(): { from: string; to: string } {
  const from = new Date();
  from.setUTCHours(0, 0, 0, 0);
  const to = new Date(from.getTime() + 180 * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}
